import { useState, useEffect, useRef, useCallback } from "react";
import type { Feature, ImportedPartFeature, MateConstraint } from "../features/featureTypes";

interface MotionStudyPanelProps {
  features: Feature[];
  onUpdateMate: (featureId: string, mateId: string, changes: Partial<MateConstraint>) => void;
}

interface AnimatableMate {
  featureId: string;
  featureName: string;
  mate: MateConstraint;
}

function findAnimatableMates(features: Feature[]): AnimatableMate[] {
  const result: AnimatableMate[] = [];
  for (const f of features) {
    if (f.type !== "importedPart") continue;
    const ip = f as ImportedPartFeature;
    for (const m of ip.mates ?? []) {
      if (m.type === "revolute" || m.type === "slider") {
        result.push({ featureId: f.id, featureName: f.name, mate: m });
      }
    }
  }
  return result;
}

export default function MotionStudyPanel({ features, onUpdateMate }: MotionStudyPanelProps) {
  const mates = findAnimatableMates(features);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [startVal, setStartVal] = useState(0);
  const [endVal, setEndVal] = useState(360);
  const [speed, setSpeed] = useState(60); // degrees/sec or mm/sec
  const [playing, setPlaying] = useState(false);
  const currentRef = useRef(0);
  const intervalRef = useRef<number | null>(null);

  const selected = mates[selectedIdx] ?? null;

  // Sync defaults when selection changes
  useEffect(() => {
    if (!selected) return;
    if (selected.mate.type === "revolute") {
      setStartVal(0);
      setEndVal(360);
      setSpeed(60);
    } else {
      setStartVal(-50);
      setEndVal(50);
      setSpeed(20);
    }
  }, [selectedIdx, selected?.mate.id]);

  const stop = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    setPlaying(false);
  }, []);

  const play = useCallback(() => {
    if (!selected) return;
    currentRef.current = startVal;
    setPlaying(true);

    const fps = 30;
    const step = speed / fps;
    const key = selected.mate.type === "revolute" ? "constraintAngle" : "constraintOffset";

    intervalRef.current = window.setInterval(() => {
      currentRef.current += step;
      if (currentRef.current >= endVal) {
        currentRef.current = startVal; // loop
      }
      onUpdateMate(selected.featureId, selected.mate.id, { [key]: currentRef.current });
    }, 1000 / fps);
  }, [selected, startVal, endVal, speed, onUpdateMate]);

  const reset = useCallback(() => {
    stop();
    if (!selected) return;
    const key = selected.mate.type === "revolute" ? "constraintAngle" : "constraintOffset";
    onUpdateMate(selected.featureId, selected.mate.id, { [key]: startVal });
  }, [selected, startVal, stop, onUpdateMate]);

  // Cleanup on unmount
  useEffect(() => () => { if (intervalRef.current) clearInterval(intervalRef.current); }, []);

  const unit = selected?.mate.type === "revolute" ? "°" : "mm";

  return (
    <div className="motion-study-panel">
      {mates.length === 0 ? (
        <div className="motion-study-empty">
          <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="0.8" width="40" height="40" opacity="0.25">
            <circle cx="24" cy="24" r="16"/>
            <path d="M24 14v10l7 4" strokeWidth="1.2"/>
            <path d="M10 24a14 14 0 0114-14" strokeDasharray="3 2"/>
            <path d="M38 24a14 14 0 01-14 14" strokeDasharray="3 2"/>
          </svg>
          <span>No animatable mates</span>
          <span className="motion-study-hint">Add a revolute or slider mate, then animate angle or offset over time</span>
        </div>
      ) : (
        <>
          <div className="motion-study-field">
            <label>Mate</label>
            <select
              className="motion-study-select"
              value={selectedIdx}
              onChange={(e) => { stop(); setSelectedIdx(Number(e.target.value)); }}
            >
              {mates.map((m, i) => (
                <option key={m.mate.id} value={i}>
                  {m.featureName} — {m.mate.type}
                </option>
              ))}
            </select>
          </div>

          <div className="motion-study-field">
            <label>Start</label>
            <input type="number" className="motion-study-number" value={startVal}
              onChange={(e) => setStartVal(Number(e.target.value))} />
            <span className="motion-study-unit">{unit}</span>
          </div>

          <div className="motion-study-field">
            <label>End</label>
            <input type="number" className="motion-study-number" value={endVal}
              onChange={(e) => setEndVal(Number(e.target.value))} />
            <span className="motion-study-unit">{unit}</span>
          </div>

          <div className="motion-study-field">
            <label>Speed</label>
            <input type="number" className="motion-study-number" min={1} max={500} value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))} />
            <span className="motion-study-unit">{unit}/s</span>
          </div>

          <div className="motion-study-controls">
            {playing ? (
              <button className="motion-study-btn motion-study-btn--stop" onClick={stop}>
                <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                  <rect x="4" y="4" width="3" height="8"/><rect x="9" y="4" width="3" height="8"/>
                </svg>
                Pause
              </button>
            ) : (
              <button className="motion-study-btn motion-study-btn--play" onClick={play}>
                <svg viewBox="0 0 16 16" fill="currentColor" width="12" height="12">
                  <path d="M5 3l8 5-8 5V3z"/>
                </svg>
                Play
              </button>
            )}
            <button className="motion-study-btn" onClick={reset}>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="12" height="12">
                <path d="M3 8a5 5 0 019.5-1.5M13 3v3.5H9.5"/><path d="M13 8a5 5 0 01-9.5 1.5M3 13V9.5h3.5"/>
              </svg>
              Reset
            </button>
          </div>
        </>
      )}
    </div>
  );
}
